import { useEffect, useRef } from 'react';
import './legacy/traceRoute.css';
import TraceRoute from './legacy/TraceRoute';

/**
 * Extracts the hop array from any of the trace-route shapes returned by the API.
 * Mirrors getTraceHops() in WebTestNowResults.jsx.
 */
function extractHops(traceRoute) {
    if (!traceRoute) return [];
    if (Array.isArray(traceRoute.hops)) return traceRoute.hops;
    if (Array.isArray(traceRoute.hop_data)) return traceRoute.hop_data;
    if (Array.isArray(traceRoute?.trace_route?.route?.hops)) return traceRoute.trace_route.route.hops;
    if (Array.isArray(traceRoute?.route?.hops)) return traceRoute.route.hops;
    if (Array.isArray(traceRoute?.traceRoute?.route?.hops)) return traceRoute.traceRoute.route.hops;
    if (Array.isArray(traceRoute.path)) return traceRoute.path;
    return [];
}

/**
 * React wrapper around the legacy KineticJS TraceRoute visualization used by the
 * portal/ui portal. Renders the per-agent horizontal trace-route path into a div
 * whose id matches what the legacy VisualizePath code expects
 * (`container-<agentId>-horizontal`).
 *
 * The legacy code attaches both a Kinetic canvas and absolutely positioned HTML
 * overlay divs to the container, so the container is `position: relative`.
 */
export default function AgentTraceRouteCanvas({ agent, traceRoute, testName, isDarkMode }) {
    const agentId = agent?.agent;
    const containerId = `container-${agentId}-horizontal`;
    const containerRef = useRef(null);

    useEffect(() => {
        const containerEl = containerRef.current;
        if (!containerEl || !agentId) return undefined;

        // Reset any prior render before redrawing.
        containerEl.innerHTML = '';

        if (!traceRoute) return undefined;

        const data = {
            ...traceRoute,
            hop_data: extractHops(traceRoute),
        };

        const quickTest = {
            name: testName || '',
            // TraceRoute() reads agentResults only to populate its load table; we
            // bypass that flow but it still needs to construct without throwing.
            agentResults: [{ agent: agentId }],
        };

        try {
            new TraceRoute(quickTest, isDarkMode).init().buildTraceRouteViz(data, agentId);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('AgentTraceRouteCanvas: failed to render trace route', err);
        }

        return () => {
            // Tear down Kinetic canvas + any HTML overlays the legacy code appended.
            if (containerEl) containerEl.innerHTML = '';
        };
    }, [agentId, traceRoute, testName, isDarkMode]);

    return (
        <div
            ref={containerRef}
            id={containerId}
            className={`probe-horizontal-container${isDarkMode ? ' dark-mode' : ''}`}
            // The legacy portal/ui canvas reserves ~290px on the left for a root
            // label block (leftMargin 30 + rootNodeWidth 235 + rootNodeHorizontalSpace 50
            // − nodeRadius 25) that we don't render in the React portal because
            // the agent name/location is shown separately in MUI. Pull the
            // whole canvas left by that amount so the first hop circle sits
            // near the visible left edge.
            style={{ position: 'relative', minHeight: 120, marginLeft: -290 }}
        />
    );
}
